/**
 * Bundle picker storefront component.
 *
 * Renders against the `bundle-product` section markup. On each radio change it
 * recomputes the bundle price preview; on add-to-cart it submits ONE cart
 * line per selected component variant, tagged with a shared `_bundle_id`
 * line item property. The Cart Transform Function (Rust, in the
 * hot-tackle-bundles-app repo) groups lines by `_bundle_id`, looks the bundle
 * up in the shop-level `hot_tackle.bundles` metafield to find the parent
 * variant + pricing, and merges them into one bundle line at checkout.
 *
 * We intentionally do NOT add the parent (bundle wrapper) variant to the
 * cart — the Cart Transform's `parentVariantId` already points at it, and
 * adding a real cart line for it would show up as a redundant "1 × Bundle"
 * sub-item under itself at checkout.
 *
 * Price math MUST match these three implementations:
 *   - hot-tackle-bundles-app/app/lib/pricing.ts                          (admin preview)
 *   - hot-tackle-bundles-app/extensions/bundle-cart-transform/src/...    (cart merge)
 *
 * Units:
 *   - data-price-cents / data-compare-at-cents are integer cents (Liquid
 *     `{{ variant.price }}` outputs cents).
 *   - data-pricing-value for FIXED is dollars (matches the JSON metafield
 *     written by bundle-sync.server.ts, which is consumed by the Rust Cart
 *     Transform as dollars).
 *
 * Two layouts, one element:
 *   - SHOWCASE (data-bundle-style="SHOWCASE") — each slot is a big card with a
 *     greyscale main image (CSS filter) and a <select> dropdown. Picking an
 *     option mirrors the value into a visually-hidden radio (so the existing
 *     selections() reader keeps working) AND swaps the slot's main image to
 *     the variant image, removing the greyscale filter.
 *   - STANDARD (default fallback) — each slot is a radio-card list, inline
 *     above the add-to-cart button. Existing pre-SHOWCASE layout.
 *
 * Multiple summary fragments (hero + picker footer in SHOWCASE) are supported
 * via querySelectorAll on the data-bundle-* selectors.
 */
(() => {
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.bundleId = this.dataset.bundleId;
      this.parentVariantId = this.dataset.parentVariantId;
      this.pricingMode = this.dataset.pricingMode;
      this.pricingValue = parseFloat(this.dataset.pricingValue) || 0;
      this.bundleStyle = (this.dataset.bundleStyle || "SHOWCASE").toUpperCase();
      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";

      this.slots = Array.from(this.querySelectorAll("fieldset.bundle-slot[data-slot-position]"));

      // Summary elements come in pairs in SHOWCASE mode (hero pricebar + picker
      // footer) — always treat as lists.
      this.priceEls = Array.from(this.querySelectorAll("[data-bundle-price]"));
      this.rrpEls = Array.from(this.querySelectorAll("[data-bundle-rrp]"));
      this.rrpLabelEls = Array.from(this.querySelectorAll("[data-bundle-rrp-label]"));
      this.savingsEls = Array.from(this.querySelectorAll("[data-bundle-savings]"));
      this.hintEls = Array.from(this.querySelectorAll("[data-bundle-hint]"));
      this.addBtns = Array.from(this.querySelectorAll("[data-bundle-add]"));
      this.addLabels = Array.from(this.querySelectorAll("[data-bundle-add-label]"));
      this.errorEls = Array.from(this.querySelectorAll("[data-bundle-error]"));

      // STANDARD bundles render no price in this picker — the theme's native
      // price block (which also carries the tax / instalments / shipping info)
      // is the single price shown, and we keep it in sync with the live
      // selection total. Snapshot its server-rendered "from" price so we can
      // restore it whenever the selection is incomplete.
      this.nativePrices = [];
      if (this.bundleStyle === "STANDARD") {
        for (const block of document.querySelectorAll("[data-bundle-native-price]")) {
          const cur = block.querySelector(".price__current .js-value");
          if (!cur) continue;
          const priceRoot = block.querySelector(".price");
          const was = block.querySelector(".price__was .js-value");
          this.nativePrices.push({
            priceRoot,
            cur,
            was,
            curHtml: cur.innerHTML,
            wasHtml: was ? was.innerHTML : "",
            onSale: priceRoot ? priceRoot.classList.contains("price--on-sale") : false,
          });
        }
      }

      this.defaultAddLabel =
        this.dataset.addLabel ||
        this.addLabels[0]?.textContent ||
        "Add bundle to cart";

      // For SHOWCASE, mirror <select> changes back into the hidden radio mirror
      // so existing selections() logic keeps working.
      this.addEventListener("change", (event) => this.onChange(event));

      for (const btn of this.addBtns) {
        btn.addEventListener("click", () => this.submitToCart());
      }

      this.render();
    }

    onChange(event) {
      const target = event.target;
      if (target && target.matches && target.matches("[data-bundle-slot-select]")) {
        this.syncSelectIntoRadios(target);
        this.swapSlotImage(target);
        this.updateRowPrice(target);
      }
      this.render();
    }

    /**
     * Mirror the <select> value into the visually-hidden radio mirror inside
     * the same slot. `selections()` reads from the radios, so this keeps the
     * SHOWCASE select-driven UI compatible without per-style branches.
     */
    syncSelectIntoRadios(selectEl) {
      const slot = selectEl.closest("fieldset.bundle-slot");
      if (!slot) return;
      const value = selectEl.value;
      const radios = slot.querySelectorAll(`input[type=radio][name="bundle-slot-${slot.dataset.slotPosition}"]`);
      for (const radio of radios) {
        radio.checked = radio.value === value && !radio.disabled;
      }
    }

    /**
     * Swap the slot's main image to the picked variant's image and remove the
     * greyscale filter (toggled by a `data-bundle-picked` attribute on the
     * fieldset). No-op if the slot has no media container (STANDARD layout).
     */
    swapSlotImage(selectEl) {
      const slot = selectEl.closest("fieldset.bundle-slot");
      if (!slot) return;
      const mediaImg = slot.querySelector("[data-bundle-slot-image]");
      const option = selectEl.options[selectEl.selectedIndex];
      const imageUrl = option?.dataset?.variantImage;
      if (mediaImg && imageUrl) {
        if (mediaImg.src !== imageUrl) {
          mediaImg.src = imageUrl;
          mediaImg.removeAttribute("srcset");
          mediaImg.removeAttribute("data-widths");
        }
      }
      if (selectEl.value) {
        slot.setAttribute("data-bundle-picked", "true");
      } else {
        slot.removeAttribute("data-bundle-picked");
      }
    }

    /**
     * STANDARD layout only: refresh the row's own price (and compare-at) to the
     * picked variant. No-op in SHOWCASE (no per-row price element). The summary
     * total is still computed from the radios in render().
     */
    updateRowPrice(selectEl) {
      const slot = selectEl.closest("fieldset.bundle-slot");
      if (!slot) return;
      const priceEl = slot.querySelector("[data-bundle-row-price]");
      if (!priceEl) return;
      const option = selectEl.options[selectEl.selectedIndex];
      if (!option || !option.value) return;
      const priceCents = parseInt(option.dataset.priceCents, 10) || 0;
      const compareCents = parseInt(option.dataset.compareAtCents, 10) || 0;
      let html = "";
      if (compareCents > priceCents) {
        html += `<s class="bundle-row__rrp">${this.formatMoney(compareCents)}</s> `;
      }
      html += this.formatMoney(priceCents);
      priceEl.innerHTML = html;
    }

    selections() {
      return this.slots.map((slot) => {
        const checked = slot.querySelector("input[type=radio]:checked");
        // A sold-out single-choice variant is rendered as checked+disabled —
        // treat that as no valid pick so the customer can't add-to-cart and
        // hit a Shopify inventory error.
        if (!checked || checked.disabled) return null;
        return {
          slotPosition: Number(slot.dataset.slotPosition),
          variantId: Number(checked.value),
          priceCents: parseInt(checked.dataset.priceCents, 10) || 0,
          compareAtCents: parseInt(checked.dataset.compareAtCents, 10) || 0,
        };
      });
    }

    computePricing(picks) {
      const subtotalCents = picks.reduce((sum, p) => sum + p.priceCents, 0);
      const compareAtCents = picks.reduce(
        (sum, p) => sum + (p.compareAtCents || p.priceCents),
        0,
      );

      let totalCents;
      if (this.pricingMode === "PERCENTAGE") {
        const pct = Math.max(0, Math.min(100, this.pricingValue));
        totalCents = Math.round(subtotalCents * (1 - pct / 100));
      } else {
        // FIXED: pricingValue is in dollars (matches Rust Cart Transform &
        // the JSON metafield written by bundle-sync.server.ts).
        const fixedCents = Math.round(this.pricingValue * 100);
        totalCents = Math.max(0, Math.min(subtotalCents, fixedCents));
      }

      const savingsCents = Math.max(0, compareAtCents - totalCents);
      const savingsPercent =
        compareAtCents > 0 ? Math.round((savingsCents / compareAtCents) * 100) : 0;

      return { subtotalCents, totalCents, compareAtCents, savingsCents, savingsPercent };
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    setText(els, value) {
      for (const el of els) {
        if (el) el.textContent = value;
      }
    }

    setHidden(els, hidden) {
      for (const el of els) {
        if (!el) continue;
        if (hidden) {
          el.setAttribute("hidden", "");
        } else {
          el.removeAttribute("hidden");
        }
      }
    }

    setDisabled(els, disabled) {
      for (const el of els) {
        if (!el) continue;
        el.disabled = disabled;
      }
    }

    /** Restore the native price block to its server-rendered "from" price
     * (used while the bundle selection is incomplete). */
    restoreNativePrices() {
      for (const n of this.nativePrices) {
        n.cur.innerHTML = n.curHtml;
        if (n.was) n.was.innerHTML = n.wasHtml;
        if (n.priceRoot) n.priceRoot.classList.toggle("price--on-sale", n.onSale);
      }
    }

    /** Mirror the live bundle total (and RRP / on-sale state) into the theme's
     * native price block. superscript_decimals is off store-wide, so a plain
     * textContent write matches the native money rendering exactly. */
    syncNativePrices(result) {
      const onSale = result.compareAtCents > result.totalCents;
      for (const n of this.nativePrices) {
        n.cur.textContent = this.formatMoney(result.totalCents);
        if (n.was) n.was.textContent = onSale ? this.formatMoney(result.compareAtCents) : "";
        if (n.priceRoot) n.priceRoot.classList.toggle("price--on-sale", onSale);
      }
    }

    /**
     * True when some slot can never be satisfied — every one of its options is
     * sold out (all radios disabled). Distinct from "not yet selected": no
     * amount of choosing fixes it, so the bundle itself is unbuyable and the
     * "please select" prompts would be misleading.
     */
    hasDeadSlot() {
      return this.slots.some((slot) => {
        const radios = slot.querySelectorAll("input[type=radio]");
        // Zero radios = a missing-product slot (component deleted/unresolved):
        // just as unbuyable as all-options-sold-out.
        return radios.length === 0 || Array.from(radios).every((r) => r.disabled);
      });
    }

    render() {
      const picks = this.selections();
      const complete = picks.length > 0 && picks.every(Boolean);
      const remaining = picks.filter((p) => !p).length;

      if (this.hasDeadSlot()) {
        this.setDisabled(this.addBtns, true);
        this.setText(this.priceEls, "");
        this.setText(this.rrpEls, "");
        this.setHidden(this.rrpEls, true);
        this.setHidden(this.rrpLabelEls, true);
        this.setText(this.savingsEls, "");
        this.setHidden(this.hintEls, true);
        this.restoreNativePrices();
        this.setText(this.addLabels, "Sold out");
        return;
      }

      this.setDisabled(this.addBtns, !complete);

      if (!complete) {
        this.setText(this.priceEls, "");
        this.setText(this.rrpEls, "");
        this.setHidden(this.rrpEls, true);
        this.setHidden(this.rrpLabelEls, true);
        this.setText(this.savingsEls, "");
        this.setHidden(this.hintEls, false);
        this.restoreNativePrices();
        const remainingLabel =
          remaining === 1
            ? "Please select 1 more option"
            : `Please select ${remaining} options`;
        this.setText(this.addLabels, remainingLabel);
        return;
      }

      this.setHidden(this.hintEls, true);
      this.setText(this.addLabels, this.defaultAddLabel);

      const result = this.computePricing(picks);
      this.setText(this.priceEls, this.formatMoney(result.totalCents));
      if (result.compareAtCents > result.totalCents) {
        this.setText(this.rrpEls, this.formatMoney(result.compareAtCents));
        this.setHidden(this.rrpEls, false);
        this.setHidden(this.rrpLabelEls, false);
      } else {
        this.setText(this.rrpEls, "");
        this.setHidden(this.rrpEls, true);
        this.setHidden(this.rrpLabelEls, true);
      }
      if (result.savingsCents > 0) {
        this.setText(
          this.savingsEls,
          `Save ${this.formatMoney(result.savingsCents)} (${result.savingsPercent}%)`,
        );
      } else {
        this.setText(this.savingsEls, "");
      }
      this.syncNativePrices(result);
    }

    async submitToCart() {
      const picks = this.selections();
      if (!picks.every(Boolean)) return;

      // `_bundle_id` identifies the bundle DEFINITION (looked up in the shop
      // metafield for pricing + parent variant). `_bundle_group` is unique to
      // THIS add-to-cart click — the Cart Transform groups by it so two adds
      // of the same bundle stay as two distinct cart lines instead of being
      // merged into one line with all components combined.
      // `_bundle_title` is display-only metadata: underscore properties are
      // hidden from customers, and the Cart Transform reads only the
      // attributes it asks for — but it survives onto the ORDER's component
      // line items, where packing slips / order printing use it to label
      // which bundle each component belongs to.
      const sharedProps = {
        _bundle_id: String(this.bundleId),
        _bundle_group: this.newBundleGroupId(),
        ...(this.dataset.bundleTitle ? { _bundle_title: this.dataset.bundleTitle } : {}),
      };
      const items = picks.map((p) => ({
        id: p.variantId,
        quantity: 1,
        properties: {
          ...sharedProps,
          _bundle_slot: String(p.slotPosition),
        },
      }));

      this.setBusy(true);
      this.hideError();

      const cartDrawer = document.querySelector("cart-drawer");
      const afterAtc = window.theme?.settings?.afterAtc;
      const cartAddUrl = window.theme?.routes?.cartAdd || "/cart/add.js";
      const cartUrl = window.theme?.routes?.cart || "/cart";

      // Section list mirrors the theme's product-form.js so cart-icon-bubble
      // and the cart drawer get refreshed HTML back from /cart/add.js.
      const sections = ["cart-icon-bubble"];
      const cartDrawerSectionId = cartDrawer
        ?.closest(".shopify-section")
        ?.id?.replace("shopify-section-", "");
      if (cartDrawerSectionId) sections.push(cartDrawerSectionId);

      try {
        const res = await fetch(cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items,
            sections: sections.join(","),
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          const message =
            (typeof data?.description === "string" && data.description) ||
            data?.message ||
            `Add to cart failed (${res.status})`;
          throw new Error(message);
        }

        if (afterAtc === "page") {
          window.location.href = cartUrl;
          return;
        }

        if (cartDrawer && typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(data, afterAtc !== "none");
          this.setBusy(false);
          this.resetSelections();
        } else {
          window.location.href = cartUrl;
        }
      } catch (err) {
        console.error("[bundle-picker] add to cart failed", err);
        this.showError(err.message || "Sorry, we couldn't add the bundle to your cart. Please try again.");
        this.setBusy(false);
      }
    }

    /**
     * Per-add-to-cart unique identifier. Concatenated of millis + random base36
     * so it's stable enough to read in cart logs but unique across submissions.
     * `crypto.randomUUID` would be cleaner but isn't available in all browsers
     * over HTTP (the Shopify storefront is HTTPS so it's fine — but the
     * fallback keeps this drop-in safe).
     */
    newBundleGroupId() {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return window.crypto.randomUUID();
      }
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    resetSelections() {
      // Only clear multi-choice slots — single-choice (locked) slots are
      // forced picks and should stay selected so the user sees the bundle
      // is still ready to add again.
      for (const slot of this.slots) {
        const selectEl = slot.querySelector("[data-bundle-slot-select]");
        const radios = slot.querySelectorAll("input[type=radio]");
        if (radios.length <= 1) continue;

        if (selectEl && !selectEl.disabled) {
          selectEl.selectedIndex = 0;
          slot.removeAttribute("data-bundle-picked");
        }
        for (const input of radios) input.checked = false;
      }
      this.render();
    }

    setBusy(busy) {
      this.setDisabled(this.addBtns, busy);
      if (busy) {
        this.setText(this.addLabels, "Adding…");
      } else {
        // render() will pick the right label based on selection state.
        this.render();
      }
    }

    showError(message) {
      for (const el of this.errorEls) {
        if (!el) continue;
        el.textContent = message;
        el.hidden = false;
      }
    }

    hideError() {
      for (const el of this.errorEls) {
        if (!el) continue;
        el.hidden = true;
        el.textContent = "";
      }
    }
  }

  if (!customElements.get("bundle-picker")) {
    customElements.define("bundle-picker", BundlePicker);
  }
})();
